// src/auth/jwt/useJwt.js
import axios from '@axios'
import axiosOrig from 'axios'
import vueCookie from 'vue-cookie'
import { TOKEN_ENDPOINT, apiUrl } from '@/config'

/**
 * - Single-flight refresh + queued retries
 * - Robust cookie ops (secure in prod)
 * - Refresh timeout to prevent deadlocks
 * - Emits `auth:session-expired` for AuthSessionListener.vue
 */

/** =================== Environment =================== */
const IS_PROD = process.env.NODE_ENV === 'production'
const REFRESH_TIMEOUT_MS = 12000

// Optional: configure cookie domain for cross-subdomain cookies (ONLY if you need it)
// Example: ".example.com"
const COOKIE_DOMAIN = process.env.VUE_APP_COOKIE_DOMAIN || ''

/**
 * sameSite = 'Lax' is correct for non-iframe normal usage.
 * secure=true in prod because you're HTTPS.
 */
const COOKIE_OPTS_BASE = {
  path: '/',
  sameSite: 'Lax',
  secure: IS_PROD,
  ...(COOKIE_DOMAIN ? { domain: COOKIE_DOMAIN } : {}),
}

/** =================== OAuth Client (from your env) =================== */
const API_CLIENT_ID = process.env.API_CLIENT_ID || null
const API_CLIENT_SECRET = process.env.API_CLIENT_SECRET || null
const API_SCOPE = process.env.API_SCOPE || null // optional

/** =================== Session marker =================== */
const HAD_SESSION_KEY = 'auth.hadSession'
const setHadSession = () => { try { localStorage.setItem(HAD_SESSION_KEY, '1') } catch (_) {} }
const clearHadSession = () => { try { localStorage.removeItem(HAD_SESSION_KEY) } catch (_) {} }
const hadSession = () => {
  try { return localStorage.getItem(HAD_SESSION_KEY) === '1' } catch (_) { return false }
}

/** =================== Config =================== */
const jwtConfig = {
  loginEndpoint: TOKEN_ENDPOINT,
  refreshEndpoint: TOKEN_ENDPOINT,
  registerEndpoint: apiUrl('register'),
  logoutEndpoint: '/jwt/logout', // kept for compatibility
  tokenType: 'Bearer',
  storageTokenKeyName: 'accessToken',
  storageRefreshTokenKeyName: 'refreshToken',
}

/** =================== Internal Flags =================== */
let isUserLoggingOut = false

let isRefreshing = false
let refreshSubscribers = []

let sessionExpiredEmitted = false
const INTERCEPTOR_FLAG = '__otj_auth_interceptor_installed__'

/** =================== Helpers =================== */
const sanitizeToken = v => {
  if (v === undefined || v === null) return null
  const s = String(v).trim()
  if (!s || s.toLowerCase() === 'undefined' || s.toLowerCase() === 'null') return null
  return s
}

const tryCookieDelete = (key, opts) => {
  try {
    if (typeof vueCookie.delete === 'function') return vueCookie.delete(key, opts)
    if (typeof vueCookie.remove === 'function') return vueCookie.remove(key, opts)
  } catch (_) {}
}

const cookieDel = key => {
  // delete host cookie
  tryCookieDelete(key, { path: '/', ...(COOKIE_DOMAIN ? { domain: COOKIE_DOMAIN } : {}) })
  // also attempt without domain (in case env changed or old cookies exist)
  tryCookieDelete(key, { path: '/' })

  // if COOKIE_DOMAIN not set, attempt base-domain deletion as best effort
  if (!COOKIE_DOMAIN && typeof window !== 'undefined') {
    const host = window.location?.hostname || ''
    const parts = host.split('.')
    if (parts.length >= 2) {
      const base = `.${parts.slice(-2).join('.')}`
      tryCookieDelete(key, { path: '/', domain: base })
    }
  }
}

/**
 * Emit session-expired ONLY if user had a session before.
 * Guests (never logged in) should NOT get "session expired".
 */
const emitSessionExpired = (reason = 'unknown') => {
  if (isUserLoggingOut) return
  if (!hadSession()) return
  if (sessionExpiredEmitted) return
  sessionExpiredEmitted = true

  try {
    window.dispatchEvent(new CustomEvent('auth:session-expired', { detail: { reason } }))
  } catch (_) {}

  setTimeout(() => { sessionExpiredEmitted = false }, 2000)
}

const clearAuth = () => {
  try { cookieDel(jwtConfig.storageTokenKeyName) } catch (_) {}
  try { cookieDel(jwtConfig.storageRefreshTokenKeyName) } catch (_) {}
  try { localStorage.removeItem('userData') } catch (_) {}
  clearHadSession()
}

const subscribeTokenRefresh = cb => {
  refreshSubscribers.push(cb)
}

const onRefreshed = newAccessToken => {
  const subs = refreshSubscribers.slice()
  refreshSubscribers = []
  subs.forEach(cb => {
    try { cb(newAccessToken) } catch (_) {}
  })
}

const withTimeout = (promise, ms, label = 'timeout') => {
  let t
  const timeout = new Promise((_, reject) => {
    t = setTimeout(() => reject(new Error(label)), ms)
  })
  return Promise.race([promise, timeout]).finally(() => clearTimeout(t))
}

const oauthCommonFields = () => {
  const out = {}
  if (API_CLIENT_ID) out.client_id = API_CLIENT_ID
  if (API_CLIENT_SECRET) out.client_secret = API_CLIENT_SECRET
  if (API_SCOPE) out.scope = API_SCOPE
  return out
}

/**
 * Clean axios instance for token operations (no app interceptors)
 */
const tokenHttp = axiosOrig.create({
  baseURL: '',
  timeout: REFRESH_TIMEOUT_MS,
  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
})

/** =================== Public JWT object =================== */
const jwt = {
  jwtConfig,

  /** ---- Token storage (Vue-Cookie) ---- */
  getToken() {
    return sanitizeToken(vueCookie.get(jwtConfig.storageTokenKeyName))
  },
  getRefreshToken() {
    return sanitizeToken(vueCookie.get(jwtConfig.storageRefreshTokenKeyName))
  },

  setToken(v, seconds) {
    const val = sanitizeToken(v)
    const key = jwtConfig.storageTokenKeyName
    if (!val) { cookieDel(key); return }

    const days = seconds ? (Math.max(1, parseInt(seconds, 10)) / 86400) : 1
    vueCookie.set(key, val, { ...COOKIE_OPTS_BASE, expires: days })
  },

  setRefreshToken(v) {
    const val = sanitizeToken(v)
    const key = jwtConfig.storageRefreshTokenKeyName
    if (!val) { cookieDel(key); return }

    vueCookie.set(key, val, { ...COOKIE_OPTS_BASE, expires: 7 })
  },

  /** ---- Auth actions ---- */

  login(payload = {}) {
    return tokenHttp.post(TOKEN_ENDPOINT, {
      grant_type: 'password',
      ...oauthCommonFields(),
      ...payload,
    }).then(res => {
      const d = res?.data || {}
      const accessToken = d.access_token || d.accessToken
      const refreshToken = d.refresh_token || d.refreshToken
      const expiresIn = d.expires_in

      if (accessToken) jwt.setToken(accessToken, expiresIn)
      if (refreshToken) jwt.setRefreshToken(refreshToken)

      // Mark that this browser has had a session
      if (accessToken || refreshToken) setHadSession()

      if (res?.data) {
        res.data.accessToken = accessToken
        res.data.refreshToken = refreshToken
      }
      return res
    })
  },

  register(...args) {
    return axios.post(jwtConfig.registerEndpoint, ...args)
  },

  refreshToken() {
    const rt = jwt.getRefreshToken()
    if (!rt) {
      clearAuth()
      emitSessionExpired('no_refresh_token')
      return Promise.reject(new Error('No refresh token'))
    }

    const req = tokenHttp.post(TOKEN_ENDPOINT, {
      grant_type: 'refresh_token',
      ...oauthCommonFields(),
      refresh_token: rt,
    })

    return withTimeout(req, REFRESH_TIMEOUT_MS, 'refresh_timeout')
      .then(res => {
        const d = res?.data || {}
        const accessToken = d.access_token || d.accessToken
        const refreshToken = d.refresh_token || d.refreshToken
        const expiresIn = d.expires_in

        if (!accessToken) {
          clearAuth()
          emitSessionExpired('refresh_no_access')
          throw new Error('Refresh returned no access token')
        }

        jwt.setToken(accessToken, expiresIn)
        if (refreshToken) jwt.setRefreshToken(refreshToken)

        // refresh success => session existed
        setHadSession()

        if (res?.data) {
          res.data.accessToken = accessToken
          res.data.refreshToken = refreshToken
        }

        return res
      })
      .catch(err => {
        const status = err?.response?.status
        if ((status === 400 || status === 401) && !isUserLoggingOut) {
          clearAuth()
          emitSessionExpired('refresh_failed')
        }
        if (!status && !isUserLoggingOut) {
          clearAuth()
          emitSessionExpired('refresh_network')
        }
        throw err
      })
  },

  logoutUser() {
    isUserLoggingOut = true
    clearAuth()
    setTimeout(() => { isUserLoggingOut = false }, 1500)
  },
}

/** =================== Axios interceptor (single-flight 401) =================== */
const installAuthInterceptorOnce = () => {
  if (axios?.[INTERCEPTOR_FLAG]) return
  axios[INTERCEPTOR_FLAG] = true

  axios.interceptors.request.use(
    cfg => {
      const t = jwt.getToken()
      if (t) {
        cfg.headers = cfg.headers || {}
        cfg.headers.Authorization = `${jwtConfig.tokenType} ${t}`
      }
      return cfg
    },
    e => Promise.reject(e),
  )

  axios.interceptors.response.use(
    res => res,
    err => {
      const response = err?.response
      const originalRequest = err?.config
      if (!response || !originalRequest) return Promise.reject(err)
      if (response.status !== 401) return Promise.reject(err)
      if (isUserLoggingOut) return Promise.reject(err)

      const url = String(originalRequest?.url || '')

      const isAuthEndpoint = url.includes(TOKEN_ENDPOINT)
        || url.includes(jwtConfig.registerEndpoint)
        || url.includes(jwtConfig.logoutEndpoint)

      if (isAuthEndpoint) {
        clearAuth()
        emitSessionExpired('auth_endpoint_401')
        return Promise.reject(err)
      }

      if (originalRequest._retry) {
        clearAuth()
        emitSessionExpired('retry_loop')
        return Promise.reject(err)
      }
      originalRequest._retry = true

      const rt = jwt.getRefreshToken()
      if (!rt) {
        clearAuth()
        emitSessionExpired('no_refresh_token')
        return Promise.reject(err)
      }

      if (!isRefreshing) {
        isRefreshing = true
        jwt.refreshToken()
          .then(r => {
            isRefreshing = false
            const newAccess = r?.data?.accessToken || r?.data?.access_token
            if (!newAccess) {
              clearAuth()
              emitSessionExpired('refresh_no_access')
              onRefreshed(null)
              return
            }
            onRefreshed(newAccess)
          })
          .catch(() => {
            isRefreshing = false
            clearAuth()
            emitSessionExpired('refresh_failed')
            onRefreshed(null)
          })
      }

      return new Promise((resolve, reject) => {
        subscribeTokenRefresh(newAccessToken => {
          if (!newAccessToken) {
            reject(err)
            return
          }
          originalRequest.headers = originalRequest.headers || {}
          originalRequest.headers.Authorization = `${jwtConfig.tokenType} ${newAccessToken}`
          resolve(axios(originalRequest))
        })
      })
    },
  )
}

installAuthInterceptorOnce()

export default jwt
